import { gql } from '@apollo/client';
import type {
  FetchResult,
  InternalRefetchQueriesInclude,
} from '@apollo/client';

import type {
  DeleteAttachmentMutation,
  DeleteAttachmentMutationVariables,
  DeleteNoteMutation,
  DeleteNoteMutationVariables,
  GetRecordViewedStatusCountsQueryVariables,
  PostAttachmentMutation,
  PostAttachmentMutationVariables,
  PostNoteMutation,
  PostNoteMutationVariables,
  SetBulkRecordViewedStatusMutation,
  SetBulkRecordViewedStatusMutationVariables,
  SetRecordViewedStatusMutation,
  SetRecordViewedStatusMutationVariables,
} from 'generated/extension.generated';
import {
  GetRecordViewedStatusCountsDocument,
  useDeleteAttachmentMutation,
  useDeleteNoteMutation,
  useGetRecordViewedStatusCountsQuery,
  usePostAttachmentMutation,
  usePostNoteMutation,
  useSetBulkRecordViewedStatusMutation,
  useSetRecordViewedStatusMutation,
} from 'generated/extension.generated';

import { useSession } from '../useSession';

function mapResourceTypeToGraphqlTypename(resourceType: string) {
  if (resourceType === 'AllergyIntolerance') {
    return 'AllergyModel';
  }
  if (resourceType === 'Appointment') {
    return 'AppointmentModel';
  }
  if (resourceType === 'Procedure') {
    return 'ProcedureModel';
  }
  if (resourceType === 'Encounter') {
    return 'EncounterModel';
  }
  if (resourceType === 'Immunization') {
    return 'ImmunizationModel';
  }
  if (resourceType === 'Condition') {
    return 'ConditionModel';
  }
  if (resourceType === 'MedicationStatement') {
    return 'MedicationStatementModel';
  }
  if (resourceType === 'FamilyMemberHistory') {
    return 'FamilyMemberHistoryModel';
  }
  if (resourceType === 'DiagnosticReport') {
    return 'DiagnosticReportModel';
  }
  if (resourceType === 'Observation') {
    return 'ObservationModel';
  }
  if (resourceType === 'Task') {
    return 'TaskModel';
  }
  if (resourceType === 'Coverage') {
    return 'CoverageModel';
  }
  if (resourceType === 'Patient') {
    return 'PatientModel';
  }
  if (resourceType === 'Consent') {
    return 'ConsentModel';
  }
}

export function usePostNote(): [
  (params: PostNoteMutationVariables) => Promise<FetchResult<PostNoteMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = usePostNoteMutation();
  const loading = baseLoading;

  async function postNote(params: PostNoteMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }
  return [postNote, loading];
}

export function useDeleteNote(): [
  (
    params: DeleteNoteMutationVariables
  ) => Promise<FetchResult<DeleteNoteMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useDeleteNoteMutation();
  const loading = baseLoading;

  async function deleteNote(params: DeleteNoteMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }
  return [deleteNote, loading];
}

export function usePostAttachment(): [
  (
    params: PostAttachmentMutationVariables
  ) => Promise<FetchResult<PostAttachmentMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = usePostAttachmentMutation();
  const loading = baseLoading;

  async function postAttachment(params: PostAttachmentMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postAttachment) {
          return;
        }
        const { resource } = params.input;
        const [resourceType, resourceId] = resource.split('/');
        const typename = mapResourceTypeToGraphqlTypename(resourceType);
        if (!typename) return;

        const normalizedResourceId = cache.identify({
          id: resourceId,
          __typename: typename,
        });

        // writeFragment works for both existing resources and newly created
        // resources where the attachments field doesn't yet exist in the cache
        // (cache.modify silently skips fields that aren't already cached)
        cache.writeFragment({
          id: normalizedResourceId,
          fragment: gql`
            fragment ResourceAttachments_${typename} on ${typename} {
              attachments {
                id
                contentType
                language
                data
                url
                size
                hash
                title
                creation
              }
            }
          `,
          data: {
            __typename: typename,
            attachments: response.postAttachment,
          },
        });
      },
    });
    return response;
  }
  return [postAttachment, loading];
}

export function useDeleteAttachment(): [
  (
    params: DeleteAttachmentMutationVariables
  ) => Promise<FetchResult<DeleteAttachmentMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useDeleteAttachmentMutation();
  const loading = baseLoading;

  async function deleteAttachment(params: DeleteAttachmentMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteAttachment) {
          return;
        }
        const normalizedId = cache.identify({
          __typename: 'Attachment',
          id: params.input.id,
        });
        cache.evict({ id: normalizedId });
        cache.gc();
      },
    });
    return response;
  }
  return [deleteAttachment, loading];
}

export function useSetViewedStatus(): [
  (
    params: SetRecordViewedStatusMutationVariables
  ) => Promise<FetchResult<SetRecordViewedStatusMutation>>,
  boolean,
] {
  const {
    state: { activeProfile },
  } = useSession();
  const [mutate, { loading: baseLoading }] = useSetRecordViewedStatusMutation();
  const loading = baseLoading;
  // Must match the SideDrawer's count query variables ("Patient/<uuid>") so Apollo updates the right cache entry.
  const queries: InternalRefetchQueriesInclude | undefined =
    activeProfile?.patient_id
      ? [
          {
            query: GetRecordViewedStatusCountsDocument,
            variables: {
              input: {
                patient_id: activeProfile.patient_id,
              },
            },
          },
        ]
      : undefined;

  async function setViewedStatus(
    params: SetRecordViewedStatusMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      refetchQueries: queries,
    });
    return response;
  }
  return [setViewedStatus, loading];
}

export function useSetBulkViewedStatus(): [
  (
    params: SetBulkRecordViewedStatusMutationVariables
  ) => Promise<FetchResult<SetBulkRecordViewedStatusMutation>>,
  boolean,
] {
  const {
    state: { activeProfile },
  } = useSession();
  const [mutate, { loading: baseLoading }] =
    useSetBulkRecordViewedStatusMutation();
  const loading = baseLoading;
  // Must match the SideDrawer's count query variables ("Patient/<uuid>") so Apollo updates the right cache entry.
  const queries: InternalRefetchQueriesInclude | undefined =
    activeProfile?.patient_id
      ? [
          {
            query: GetRecordViewedStatusCountsDocument,
            variables: {
              input: {
                patient_id: activeProfile.patient_id,
              },
            },
          },
        ]
      : undefined;

  async function setBulkViewedStatus(
    params: SetBulkRecordViewedStatusMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      refetchQueries: queries,
    });
    return response;
  }
  return [setBulkViewedStatus, loading];
}

export function useGetViewedStatusCounts(
  params: GetRecordViewedStatusCountsQueryVariables
) {
  const response = useGetRecordViewedStatusCountsQuery({
    variables: { ...params },
    skip: !params.input.patient_id,
  });
  return response;
}
